import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import MultiPort from "~/features/autostoreBin/MultiPort";
import { getFirstDefinedValue } from "~/lib/helpers";
import { mixpanelTrack, useMixpanelModalName } from "~/lib/mixpanel-tracking";

import { selectIsShortSideBin } from "~/redux/selectors/workstationsSelectors";
import { NextEmptyBinResponse, WorkstationSummaryDto } from "~/types/api";

type Props = {
  isOpen: boolean;
  setIsBinNotEmptyCompartmentPanelOpen: Dispatch<SetStateAction<boolean>>;
  setIsBinNotEmptyPanelOpen: Dispatch<SetStateAction<boolean>>;
  workstation: WorkstationSummaryDto | null;
  nextEmptyBinByPort: {
    [portId: number]: NextEmptyBinResponse | null;
  };
  newSelectedBin: NextEmptyBinResponse | null;
  setNewSelectedBin: Dispatch<SetStateAction<NextEmptyBinResponse | null>>;
  newSelectedCompartment: number | null;
  setNewSelectedCompartment: (compartment: number | null) => void;
};

function BinNotEmptyCompartment(props: Props) {
  const {
    isOpen,
    nextEmptyBinByPort,
    workstation,
    newSelectedBin,
    setNewSelectedBin,
    newSelectedCompartment,
    setNewSelectedCompartment,
    setIsBinNotEmptyCompartmentPanelOpen
  } = props;
  const { t } = useTranslation();
  const isShortSideBin = useAppSelector(selectIsShortSideBin);
  const inductionSelectedBin = useAppSelector(
    (state) => state.autostore.currentEmptyBin
  );
  const inductionSelectedBinId = inductionSelectedBin?.openBinResponse.binId;
  const trackedModalName = useMixpanelModalName("Select Incorrect Compartment");

  const handleClose = () => {
    mixpanelTrack({
      trackedPageName: trackedModalName,
      type: "Button Click",
      label: "Close"
    });
    setIsBinNotEmptyCompartmentPanelOpen(false);
    setNewSelectedCompartment(null);
    setNewSelectedBin(null);
  };

  const handleConfirm = () => {
    const binId = newSelectedBin?.openBinResponse.binId;
    mixpanelTrack({
      trackedPageName: trackedModalName,
      type: "Button Click",
      label: "Confirm",
      eventProperties: {
        BinId: binId,
        "Bin Configuration": newSelectedBin?.autostoreBinConfiguration,
        SameAsInductionSelectedBinId:
          inductionSelectedBinId != null && binId === inductionSelectedBinId
      }
    });
    props.setIsBinNotEmptyCompartmentPanelOpen(false);
    props.setIsBinNotEmptyPanelOpen(true);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>{t("select incorrect compartment")}</DialogTitle>
      <DialogContent
        sx={{
          margin: "30px 30px 0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center"
        }}
      >
        <MultiPort
          nextEmptyBinByPort={nextEmptyBinByPort}
          workstation={workstation}
          onBinClick={(compartment: number | undefined) => {
            if (compartment || compartment === 0) {
              mixpanelTrack({
                trackedPageName: trackedModalName,
                type: "Button Click",
                label: "Select Bin Compartment",
                eventProperties: {
                  Compartment: compartment,
                  BinId: newSelectedBin?.openBinResponse.binId
                }
              });
              setNewSelectedCompartment(compartment);
            }
          }}
          setSelectedBinCallback={(bin: NextEmptyBinResponse) =>
            setNewSelectedBin(bin)
          }
          selectedCompartment={getFirstDefinedValue(
            [newSelectedCompartment],
            undefined
          )}
          currentSelectedBinId={newSelectedBin?.openBinResponse.binId}
          allowSelectingCompartmentsWithoutInventoryOnly
          isShortSideBin={isShortSideBin}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", padding: "30px" }}>
        <Button
          size="large"
          variant="contained"
          onClick={handleConfirm}
          disabled={!newSelectedCompartment && newSelectedCompartment !== 0}
          sx={{
            fontWeight: "normal"
          }}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BinNotEmptyCompartment;
